import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function OswalSabha() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const sabhaNames = useMemo(
    () =>
      new Set([
        "prakash ji kothari",
        "dr. pramila jain",
        "mr. narendra kothari",
      ]),
    [],
  );

  useEffect(() => {
    api
      .get("/team")
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) => a.order - b.order);
        setTeam(sorted.filter((m) => sabhaNames.has((m.name || "").toLowerCase())));
      })
      .finally(() => setLoading(false));
  }, [sabhaNames]);

  return (
    <div data-testid="oswal-sabha-page">
      <section className="container-px pt-16 md:pt-24 pb-12">
        <div className="max-w-3xl">
          <div className="overline mb-6">Oswal Sabha</div>

          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-ink leading-[1.05]">
            Sabha leadership.
          </h1>

          <p className="mt-6 text-lg text-ink-soft leading-relaxed">
            The senior council members guiding the broader Oswal Sabha vision,
            community coordination, and long-term development.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="/teampdf.pdf"
              target="_blank"
              rel="noreferrer"
              className="btn-primary inline-flex"
            >
              <FileText className="w-4 h-4" />
              View full team list
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link to="/team" className="btn-secondary inline-flex">
              Back to council
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-px pb-24">
        {loading ? (
          <div className="text-ink-muted">Loading Sabha leadership…</div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {team.map((m, i) => (
              <motion.article
                key={m.id}
                variants={item}
                className="card-premium transition duration-300 group"
                data-testid={`oswal-sabha-member-${i}`}
              >
                <div className="relative w-full aspect-[4/5] rounded-xl overflow-hidden bg-cream-deep mb-5">
                  {m.avatar ? (
                    <img
                      src={m.avatar}
                      alt={m.name}
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-cream-deep via-white to-cream-deep" />
                  )}
                </div>

                <div className="text-xs uppercase tracking-widest text-brand mb-1">
                  {m.role}
                </div>
                <h3 className="font-heading text-xl text-ink">{m.name}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed line-clamp-4">
                  {m.bio}
                </p>
              </motion.article>
            ))}
          </motion.div>
        )}
      </section>
    </div>
  );
}
